import type { CollaborationLoginSession } from './types'

const SESSION_KEY = 'collaboration-h5-login-session'
const SESSION_TTL = 5 * 60 * 1000
const WORKSPACE_PATH = '/packageMain/pages/workspace/index'
declare const __uniRoutes: { path: string; alias?: string; meta?: { route?: string } }[]

/**
 * 保存授权跳转前的 H5 页面状态，避免页面恢复时重复发起 OAuth。
 */
export function saveCollaborationLoginSession(returnUrl: string): void {
    try {
        const session: CollaborationLoginSession = {
            returnUrl,
            startedAt: Date.now()
        }
        sessionStorage.setItem(SESSION_KEY, JSON.stringify(session))
    } catch {
        // Storage unavailable should not block the OAuth login flow.
    }
}

/**
 * 获取仍有效的授权跳转前 H5 页面状态。
 */
export function getCollaborationLoginSession(): CollaborationLoginSession | null {
    try {
        const rawValue = sessionStorage.getItem(SESSION_KEY)
        if (!rawValue) return null
        const session = JSON.parse(rawValue) as CollaborationLoginSession
        if (!Number.isFinite(session.startedAt) || session.startedAt > Date.now() || Date.now() - session.startedAt >= SESSION_TTL || typeof session.returnUrl !== 'string') {
            clearCollaborationLoginSession()
            return null
        }
        return session
    } catch {
        return null
    }
}

/**
 * 清理 H5 协同登录页面状态。
 */
export function clearCollaborationLoginSession(): void {
    try {
        sessionStorage.removeItem(SESSION_KEY)
    } catch {
        // Storage unavailable should not block the OAuth login flow.
    }
}

/**
 * 将当前 H5 URL 转换为可由后端校验的站内路由路径。
 */
export function resolveCollaborationReturnUrl(rawValue?: string): string {
    if (!rawValue) return '/'

    try {
        let value = rawValue
        // Only decode a historically encoded whole address, never its business query.
        for (let layer = 0; layer < 2 && /^(?:https?%|%2f)/i.test(value); layer++) {
            value = decodeURIComponent(value)
        }
        if (/[\\\u0000-\u0020]/.test(value) || value.startsWith('//')) return '/'
        const url = new URL(value, location.origin)
        if (url.origin !== location.origin) return '/'
        const route = url.hash.startsWith('#/') ? url.hash.slice(1) : url.pathname + url.search
        const queryIndex = route.indexOf('?')
        const path = queryIndex < 0 ? route : route.slice(0, queryIndex)
        if (!path.startsWith('/') || path.startsWith('/packageLogin/') ||
            path === '/packageUser/pages/collaboration-binding/index' ||
            path === '/packageUser/pages/social/index') return '/'
        if (typeof __uniRoutes === 'undefined' || !__uniRoutes.some(item =>
            item.path === path || '/' + item.meta?.route === path
        )) return '/'
        if (queryIndex < 0) return path
        // Preserve encoded business values exactly; remove only OAuth protocol fields.
        const query = route.slice(queryIndex + 1).split('&').filter(part => {
            const key = decodeURIComponent(part.split('=')[0])
            return !['code', 'state', 'error', 'error_description', 'noOauth2', 'tenantId', 'loginIntent'].includes(key)
        }).join('&')
        return path + (query ? '?' + query : '')
    } catch {
        return '/'
    }
}

/**
 * 跳转到协同登录前保留的站内路由。
 */
export async function redirectAfterCollaborationLogin(returnUrl: string): Promise<void> {
    const path = resolveCollaborationReturnUrl(returnUrl)
    if (typeof location.href === 'string' && window.history?.replaceState) {
        const clean = new URL(location.href)
        for (const key of ['code', 'state', 'error', 'error_description', 'noOauth2', 'tenantId', 'redirect', 'loginIntent']) {
            clean.searchParams.delete(key)
        }
        window.history.replaceState(window.history.state, '', clean.toString())
    }
    await uni.reLaunch({ url: path === '/' ? WORKSPACE_PATH : path })
}

export function readCollaborationLoginQuery(href: string): URLSearchParams {
    const url = new URL(href)
    const query = new URLSearchParams(url.searchParams)
    const index = url.hash.indexOf('?')
    if (index >= 0) new URLSearchParams(url.hash.slice(index + 1)).forEach((value, key) => query.set(key, value))
    return query
}

/** Keep the deployed document path; callback query values are encoded exactly once. */
export function buildCollaborationCallbackUri(page: string, query: URLSearchParams): string {
    const url = new URL(location.origin)
    if (typeof location.href === 'string') {
        const current = new URL(location.href)
        if (current.origin === url.origin) url.pathname = current.pathname
    }
    url.hash = page + '?' + query.toString()
    return url.toString()
}

/** 旧业务入口的 noOauth2 不是认证页的密码登录指令。 */
export function isPasswordLoginEntry(href: string): boolean {
    const url = new URL(href)
    const path = url.hash.split('?')[0]
    const authenticationEntry = ['', '#', '#/', '#/packageLogin/pages/session/index', '#/packageLogin/pages/login/index'].includes(path)
    if (!authenticationEntry) return false
    const query = readCollaborationLoginQuery(href)
    return query.get('loginIntent') === 'password' ||
        (query.get('loginIntent') !== 'authorize' && query.get('noOauth2') === '1')
}

/**
 * Recover old links with an absolute address embedded in the hash.
 * Discard old OAuth codes rather than redeeming them again.
 */
export function recoverCollaborationRoute(): void {
    const url = new URL(location.href)
    if (!/^#\/https?(?::|%)/i.test(url.hash)) return
    const path = resolveCollaborationReturnUrl(url.hash.slice(2))
    url.search = ''
    url.hash = path === '/' ? '/packageLogin/pages/oauth2/index?noOauth2=1' : path
    window.history.replaceState(window.history.state, '', url.toString())
}
